import React, { useCallback, useEffect, useState } from "react";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { LAMPORTS_PER_SOL } from "@solana/web3.js";
import { slurpApi } from "../../lib/slurpApi";
import { readPendingFees, claimFees, claimAndBurnFees } from "../../lib/solanaFees";
import PremiumCard from "./PremiumCard";

/**
 * Token card + per-launch claim widget. Used on the profile page's
 * "Launched" tab so the wallet that created the token can see pending
 * SOL fees and claim them (or, for burn-mode launches, see the
 * "Fees auto-burnt" badge instead — autoBurn worker handles those).
 *
 * `viewerIsOwner` gates the claim UI: only the connected wallet that
 * actually owns the position NFT sees the claim button. Visiting
 * someone else's profile shows just the card without the claim widget.
 */
export default function LaunchCardWithClaim({
  rawToken,
  adapted,
  viewerIsOwner = true,
  /* When the parent (ProfilePage) batch-fetches pending fees server-side
     it passes the lamports value here. We use it instead of firing our
     own RPC read on mount — solves the 20+ cards × 2 RPC = Helius 429
     storm problem on creator profiles. Falls back to a per-card RPC
     read if the prop isn't supplied (for any non-profile usage). */
  prefetchedPendingLamports,
}) {
  const wallet = useWallet();
  const { connection } = useConnection();
  const [fees, setFees] = useState({ pendingSolRaw: prefetchedPendingLamports ?? "0" });
  const [claim, setClaim] = useState({ state: "idle", sig: null, error: null, solGained: "0", tokensBurnedRaw: "0" });

  const refreshFees = useCallback(async () => {
    if (!viewerIsOwner) return;
    if (!rawToken?.pool || !rawToken?.position) return;
    try {
      const res = await readPendingFees({
        connection,
        positionAddress: rawToken.position,
        poolAddress: rawToken.pool,
      });
      setFees({ pendingSolRaw: res.pendingSolRaw });
    } catch {
      /* swallow — stale read is fine */
    }
  }, [connection, rawToken?.pool, rawToken?.position, viewerIsOwner]);

  // Use the value the parent (ProfilePage) prefetched via its single
  // batched /api/portfolio/:wallet/pending-fees call. That arrives async
  // — until then this card displays 0 (acceptable: a momentary 0 on
  // first paint is far better than firing 20+ getAccountInfo calls in
  // parallel and triggering Helius 429s). Once the prefetch lands,
  // ProfilePage re-renders and the new prop value flows through here.
  // No fallback RPC on mount: the previous version of this code did,
  // which is exactly what re-created the fan-out we were trying to fix.
  // Post-claim refresh is handled by `await refreshFees()` inside
  // doClaim — one RPC call for the one card being claimed, not a
  // mass fan-out across every card on the page.
  useEffect(() => {
    if (!viewerIsOwner) return;
    if (prefetchedPendingLamports != null) {
      setFees({ pendingSolRaw: String(prefetchedPendingLamports) });
    }
  }, [viewerIsOwner, prefetchedPendingLamports]);

  const isBurnMode = rawToken?.fee_mode === "burn";
  const isLpCompoundMode = rawToken?.fee_mode === "lp_compound";
  const isAutoMode = isBurnMode || isLpCompoundMode;

  const doClaim = useCallback(async () => {
    if (!wallet.publicKey || !rawToken?.pool || !rawToken?.position || !rawToken?.position_nft) return;
    setClaim({ state: "confirming", sig: null, error: null, solGained: "0", tokensBurnedRaw: "0" });
    try {
      if (isBurnMode) {
        const res = await claimAndBurnFees({
          connection,
          wallet,
          poolAddress: rawToken.pool,
          positionAddress: rawToken.position,
          positionNftMint: rawToken.position_nft,
          tokenMint: rawToken.mint,
          tokenDecimals: Number(rawToken.decimals ?? 6),
        });
        setClaim({
          state: "success",
          sig: res.claimSig,
          error: null,
          solGained: "0",
          tokensBurnedRaw: res.tokensBurnedRaw || "0",
        });
        void slurpApi.postBurnReceipt(rawToken.mint, res.claimSig).catch(() => {});
      } else {
        const res = await claimFees({
          connection,
          wallet,
          poolAddress: rawToken.pool,
          positionAddress: rawToken.position,
          positionNftMint: rawToken.position_nft,
        });
        setClaim({
          state: "success",
          sig: res.claimSig,
          error: null,
          solGained: res.solGained || "0",
          tokensBurnedRaw: "0",
        });
      }
      await refreshFees();
    } catch (err) {
      setClaim({
        state: "error",
        sig: null,
        error: err?.message || "Claim failed",
        solGained: "0",
        tokensBurnedRaw: "0",
      });
    }
  }, [wallet, connection, rawToken, refreshFees, isBurnMode]);

  const pendingSol = Number(fees.pendingSolRaw) / LAMPORTS_PER_SOL;
  const hasPending = Number(fees.pendingSolRaw) > 0;
  const busy = claim.state === "confirming";
  const tokensBurnedWhole = claim.tokensBurnedRaw
    ? Number(claim.tokensBurnedRaw) / 10 ** Number(rawToken?.decimals ?? 6)
    : 0;

  // Public viewer (not the creator) — just show the card.
  if (!viewerIsOwner) {
    return (
      <div className="pm-launch-cell">
        <PremiumCard token={adapted} />
      </div>
    );
  }

  return (
    <div className="pm-launch-cell">
      <PremiumCard token={adapted} />
      <div className="pm-launch-fees">
        <div className="pm-launch-fees__info">
          <span className="pm-launch-fees__label">Fees generated</span>
          <span className="pm-launch-fees__amount">{pendingSol.toFixed(4)} SOL</span>
        </div>
        {isBurnMode ? (
          <span
            className="pm-launch-fees__btn pm-launch-fees__btn--burn pm-launch-fees__btn--auto"
            title="Slurp auto buys back + burns these fees forever — no creator action needed"
          >
            Fees auto-burnt
          </span>
        ) : isLpCompoundMode ? (
          <span
            className="pm-launch-fees__btn pm-launch-fees__btn--lp pm-launch-fees__btn--auto"
            title="Fees auto-compound into the LP — pool deepens forever, creator earns nothing in SOL"
          >
            Fees grow LP
          </span>
        ) : (
          <button
            className="pm-launch-fees__btn"
            disabled={busy || !hasPending}
            onClick={(e) => { e.stopPropagation(); void doClaim(); }}
            title="Claim pending SOL fees to your wallet"
          >
            {busy ? "Claiming…" : "Claim"}
          </button>
        )}
      </div>
      {claim.state === "success" && claim.sig && (
        <div className="pm-launch-fees__msg pm-launch-fees__msg--ok">
          {isBurnMode
            ? `${tokensBurnedWhole.toFixed(2)} ${rawToken?.symbol || "tokens"} burned`
            : `+${(Number(claim.solGained) / LAMPORTS_PER_SOL).toFixed(6)} SOL claimed`}
        </div>
      )}
      {claim.state === "error" && claim.error && (
        <div className="pm-launch-fees__msg pm-launch-fees__msg--err">{claim.error}</div>
      )}
    </div>
  );
}
